import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import ReactQuill from "react-quill";
import Select from "react-select";
import { Bounce, toast } from "react-toastify";
import "react-quill/dist/quill.snow.css";

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const TEST_EMAIL = "webanalytis@gmail.com";
const SENDER_EMAIL = "aida@crea.dz";
const COPY_EMAIL = "idenetoran@gmail.com";

const DEFAULT_EMAIL_HTML = `
  <p>Bonjour,</p>
  <p>Votre espace personnel est desormais disponible sur la plateforme AIDA.</p>
  <p>Vous pouvez vous connecter afin de consulter votre profil, completer vos informations et suivre les services mis a votre disposition.</p>
  <p><strong>Vos informations d'acces :</strong></p>
  <ul>
    <li><strong>Email de connexion :</strong> {{email}}</li>
    <li><strong>Nom d'utilisateur :</strong> {{username}}</li>
    <li><strong>Lien de connexion :</strong> <a href="{{loginUrl}}">{{loginUrl}}</a></li>
  </ul>
  <p>Si vous souhaitez definir ou modifier votre mot de passe, veuillez utiliser le lien suivant :</p>
  <p><a href="{{forgotPasswordUrl}}"><strong>Changer mon mot de passe AIDA</strong></a></p>
  <p>Ce lien vous permettra d'acceder a la procedure securisee de changement du mot de passe.</p>
`;

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

const htmlToText = (html: string) => {
  const root = document.createElement("div");
  root.innerHTML = html;
  return root.textContent?.replace(/\n{3,}/g, "\n\n").trim() || "";
};

interface UserOption {
  value: number;
  label: string;
  email: string;
  role: string;
}

interface DispatchResult {
  id_user?: number;
  email: string;
  username: string;
  sent: boolean;
  test?: boolean;
  error?: string;
}

type DispatchMode = "test" | "selected" | "all_clients";

const selectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: "46px",
    borderColor: "#d1d5db",
    borderRadius: "8px",
    boxShadow: "none",
    "&:hover": { borderColor: "#9ca3af" },
  }),
  menu: (base: any) => ({ ...base, zIndex: 20 }),
};

const EmailDispatch: React.FC = () => {
  const [mode, setMode] = useState<DispatchMode>("test");
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [subject, setSubject] = useState("Acces a votre espace AIDA");
  const [htmlMessage, setHtmlMessage] = useState(DEFAULT_EMAIL_HTML);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<DispatchResult[]>([]);
  const [summary, setSummary] = useState("");

  const canSend = useMemo(() => {
    if (sending) return false;
    if (mode === "selected") return selectedUsers.length > 0;
    return htmlToText(htmlMessage).length > 0;
  }, [htmlMessage, mode, selectedUsers.length, sending]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await fetch(
          `${backendUrl}/api/users?page=1&limit=500&sortColumn=id_user&sortOrder=ASC&search=&type=1`,
          { credentials: "include" },
        );
        const data = await response.json();

        const options: UserOption[] = (Array.isArray(data) ? data : [])
          .filter((user) => user.id_user !== 1 && user.email_user)
          .map((user) => ({
            value: user.id_user,
            label: `#${user.id_user} - ${user.username_user} (${user.email_user})`,
            email: user.email_user,
            role: user.role_name || "",
          }));

        setUsers(options);
      } catch (error) {
        console.error("Error loading users for email dispatch:", error);
        toast.error("Impossible de charger les utilisateurs", {
          position: "bottom-right",
          autoClose: 2400,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, []);

  const sendDispatch = async () => {
    if (!canSend) return;

    try {
      setSending(true);
      setResults([]);
      setSummary("");

      const response = await fetch(`${backendUrl}/api/email-dispatch`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode,
          userIds: selectedUsers.map((user) => user.value),
          testEmail: TEST_EMAIL,
          subject,
          message: htmlToText(htmlMessage),
          htmlMessage,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        toast.error(data.message || "Erreur pendant l'envoi", {
          position: "bottom-right",
          autoClose: 3200,
          theme: "light",
          transition: Bounce,
        });
      } else {
        toast.success("Envoi termine", {
          position: "bottom-right",
          autoClose: 2400,
          theme: "light",
          transition: Bounce,
        });
      }

      setResults(Array.isArray(data.results) ? data.results : []);
      setSummary(
        mode === "test"
          ? `Test envoye vers ${TEST_EMAIL}, copie a ${COPY_EMAIL}`
          : `${data.sent || 0}/${data.requested || 0} email(s) envoye(s), copie a ${COPY_EMAIL}`,
      );
    } catch (error) {
      console.error("Error sending email dispatch:", error);
      toast.error("Erreur serveur pendant l'envoi", {
        position: "bottom-right",
        autoClose: 3200,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container-fluid py-4 px-4 email-dispatch-page">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="mb-1">
            <i className="las la-envelope-open-text mr-2"></i>
            Dispatch emails
          </h4>
          <div className="text-muted">
            Envoi de la communication AIDA depuis {SENDER_EMAIL}. Copie a{" "}
            {COPY_EMAIL}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-7 mb-4">
          <div className="email-dispatch-panel">
            <Form.Group className="mb-4">
              <Form.Label className="font-weight-bold">Mode d'envoi</Form.Label>
              <div className="email-mode-group">
                <button
                  type="button"
                  className={mode === "test" ? "active" : ""}
                  onClick={() => setMode("test")}
                >
                  <i className="las la-vial"></i>
                  Test
                </button>
                <button
                  type="button"
                  className={mode === "selected" ? "active" : ""}
                  onClick={() => setMode("selected")}
                >
                  <i className="las la-user-check"></i>
                  Selection
                </button>
                <button
                  type="button"
                  className={mode === "all_clients" ? "active" : ""}
                  onClick={() => setMode("all_clients")}
                >
                  <i className="las la-users"></i>
                  Tous clients
                </button>
              </div>
            </Form.Group>

            {mode === "test" && (
              <Alert variant="warning" className="email-dispatch-alert">
                Le test envoie un exemple vers {TEST_EMAIL}. Une copie sera
                envoyee a {COPY_EMAIL}. Aucun mot de passe client n'est modifie.
              </Alert>
            )}

            {mode === "selected" && (
              <Form.Group className="mb-4">
                <Form.Label className="font-weight-bold">
                  Clients a traiter
                </Form.Label>
                <Select
                  isMulti
                  isLoading={loadingUsers}
                  options={users}
                  value={selectedUsers}
                  onChange={(options) =>
                    setSelectedUsers([...(options as UserOption[])])
                  }
                  styles={selectStyles}
                  placeholder="Selectionner un ou plusieurs clients"
                />
              </Form.Group>
            )}

            {mode === "all_clients" && (
              <Alert variant="danger" className="email-dispatch-alert">
                Cet envoi diffuse le message a tous les fournisseurs et OEM avec
                email valide. Une copie sera envoyee a {COPY_EMAIL}.
              </Alert>
            )}

            <Form.Group className="mb-4">
              <Form.Label className="font-weight-bold">Sujet</Form.Label>
              <Form.Control
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="font-weight-bold">
                Editeur HTML
              </Form.Label>
              <ReactQuill
                theme="snow"
                value={htmlMessage}
                onChange={setHtmlMessage}
                modules={quillModules}
                formats={quillFormats}
                style={{ background: "#fff", minHeight: 360 }}
              />
            </Form.Group>

            <Button
              type="button"
              className="email-dispatch-send"
              onClick={sendDispatch}
              disabled={!canSend}
            >
              <i className="las la-paper-plane"></i>
              {sending
                ? "Envoi..."
                : mode === "test"
                  ? "Envoyer le test"
                  : "Envoyer le message"}
            </Button>
          </div>
        </div>

        <div className="col-lg-5 mb-4">
          <div className="email-preview">
            <div className="email-preview-header">
              <div className="email-preview-brand-row">
                <img
                  src="/asset/images/cre-logo.png"
                  alt="CREA - Conseil du Renouveau Economique Algerien"
                  className="email-preview-logo"
                />
                <div className="email-preview-aida">
                  <strong>
                    <span>AI</span>DA
                  </strong>
                  <small>{SENDER_EMAIL}</small>
                </div>
              </div>
            </div>
            <div className="email-preview-body">
              <div dangerouslySetInnerHTML={{ __html: htmlMessage }} />
              <small>Cordialement, l'equipe AIDA</small>
            </div>
          </div>
        </div>
      </div>

      {summary && (
        <div className="email-dispatch-panel">
          <h5 className="mb-3">{summary}</h5>
          <div className="table-responsive">
            <Table className="table align-middle">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Username</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, index) => (
                  <tr key={`${result.email}-${index}`}>
                    <td>{result.id_user || "-"}</td>
                    <td>{result.email}</td>
                    <td>{result.username}</td>
                    <td>
                      <span
                        className={
                          result.sent
                            ? "email-status email-status-ok"
                            : "email-status email-status-error"
                        }
                      >
                        {result.sent ? "Envoye" : result.error || "Non envoye"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDispatch;
